import { useCallback, useEffect, useMemo, useRef } from "react";
import { DraftScope } from "../types";
import { deriveDraftKey } from "../utils/deriveDraftKey";

const DEBOUNCE_MS = 750;

const parseDraft = <T>(raw: string | null): T | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
};

export const useDraftPersistence = (scope: DraftScope, namespace: string) => {
  const { payeeId, contextId, formId, version } = scope;
  const key = useMemo(
    () => deriveDraftKey({ payeeId, contextId, formId, version }, namespace),
    [payeeId, contextId, formId, version, namespace]
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const read = useCallback(<T = Record<string, unknown>>(): T | null => {
    try {
      return parseDraft<T>(localStorage.getItem(key));
    } catch {
      return null;
    }
  }, [key]);

  const write = useCallback(
    (values: Record<string, unknown>) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        try {
          localStorage.setItem(key, JSON.stringify(values));
        } catch {
          // localStorage may be unavailable (quota, privacy mode) — silent
        }
      }, DEBOUNCE_MS);
    },
    [key]
  );

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
  }, [key]);

  // Cancel any pending debounced write on unmount so it can't fire afterwards.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return { read, write, clear, key };
};
